import { Textarea } from "../../ui/textarea";
import { Copy, Check, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";

const PostIconButton = ({ onClick, disabled, globalStatus, postResetKey }) => {
  const [localStatus, setLocalStatus] = useState("idle");

  useEffect(() => {
    setLocalStatus("idle");
  }, [postResetKey]);

  let normalizedGlobal = globalStatus;
  if (normalizedGlobal === true) normalizedGlobal = "success";
  if (normalizedGlobal === false) normalizedGlobal = "error";

  const handleClick = () => {
    if (localStatus === "posting" || normalizedGlobal === "posting") return;

    setLocalStatus("posting");
    onClick(
      () => {
        setLocalStatus("success");
        setTimeout(() => setLocalStatus("idle"), 3000);
      },
      () => {
        setLocalStatus("error");
        setTimeout(() => setLocalStatus("idle"), 3000);
      }
    );
  };

  let effectiveStatus = localStatus;
  if (
    normalizedGlobal === "success" ||
    normalizedGlobal === "error" ||
    normalizedGlobal === "posting"
  ) {
    effectiveStatus = normalizedGlobal;
  }

  let bgClass =
    "bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-400";
  let icon = <Send className="w-3.5 h-3.5" />;
  let label = null;

  if (disabled || normalizedGlobal === "posting") {
    bgClass = "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed";
  } else if (effectiveStatus === "success") {
    bgClass =
      "bg-green-50 text-green-700 border-green-300 w-auto px-2 hover:bg-green-100 hover:border-green-400 cursor-pointer";
    icon = <CheckCircle2 className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Success</span>;
  } else if (effectiveStatus === "error") {
    bgClass =
      "bg-red-50 text-red-700 border-red-300 w-auto px-2 hover:bg-red-100 cursor-pointer";
    icon = <AlertCircle className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Failed</span>;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || normalizedGlobal === "posting"}
      title="Post to EHR"
      className={`inline-flex items-center justify-center h-7 rounded-full border transition-all ml-2 ${bgClass} ${
        effectiveStatus === "idle" ? "w-7" : ""
      }`}
    >
      {icon}
      {label}
    </button>
  );
};

const CopyIconButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!text}
      title={label ? `Copy ${label}` : "Copy"}
      className={`inline-flex items-center justify-center h-7 rounded-full border transition-all whitespace-nowrap ${
        copied
          ? "bg-green-50 text-green-700 border-green-300"
          : "bg-white text-blue-500 border-blue-200 hover:text-blue-700 hover:border-blue-400"
      } ${copied ? "px-2 gap-1.5" : "w-7"}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied && (
        <span className="text-[10px] font-semibold uppercase tracking-wide">
          Copied
        </span>
      )}
    </button>
  );
};

const SubjectiveSection = ({
  soapNotes,
  setSoapNotes,
  isEditing,
  onPost,
  sectionStatuses = {},
  postResetKey,
}) => {
  const subj = soapNotes.subjective || {};
  const [postedItems, setPostedItems] = useState({});

  const setField = (key, value) =>
    setSoapNotes({ ...soapNotes, subjective: { ...subj, [key]: value } });

  const quote = (cc) => {
    if (!cc) return "";
    const clean = cc.trim().replace(/^["']|["']$/g, "");
    return `"${clean.charAt(0).toUpperCase() + clean.slice(1)}"`;
  };

  const renderROS = (rosText) => {
    if (!rosText) return null;
    return rosText
      .split("\n")
      .filter(Boolean)
      .map((line, idx) => {
        const [system, findings] = line.split(":").map((x) => x.trim());
        return (
          <p key={idx} className="ml-4 text-[15px] leading-relaxed">
            <span className="font-semibold">{system}:</span> {findings}
          </p>
        );
      });
  };

  const handleSectionPost = (type, content) => (onSuccess, onError) => {
    const isAlreadyPosted = sectionStatuses[type] === "success" || postedItems[type];

    onPost(
      {
        type,
        content,
        alreadyPosted: isAlreadyPosted,
      },
      () => {
        setPostedItems((prev) => ({ ...prev, [type]: true }));
        onSuccess();
      },
      onError
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-5 text-slate-800">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h3 className="text-lg font-bold text-slate-900">Subjective</h3>
        {isEditing && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
            Editing Mode
          </span>
        )}
      </div>
      {!isEditing ? (
        <>
          {subj.chief_complaint && (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Chief Complaint</p>
                <div className="flex items-center">
                  <CopyIconButton
                    text={subj.chief_complaint}
                    label="Chief Complaint"
                  />
                  {onPost && (
                    <PostIconButton
                      onClick={handleSectionPost(
                        "Chief Complaint",
                        subj.chief_complaint
                      )}
                      disabled={!subj.chief_complaint}
                      globalStatus={
                        sectionStatuses["Chief Complaint"] || "idle"
                      }
                      postResetKey={postResetKey}
                    />
                  )}
                </div>
              </div>
              <p className="text-base italic text-slate-800 leading-relaxed font-medium bg-slate-50/60 p-3 rounded-lg border border-slate-100">
                {quote(subj.chief_complaint)}
              </p>
            </>
          )}

          {subj.hpi && (
            <>
              <div className="flex items-center justify-between gap-3 mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  History of Present Illness (HPI)
                </p>
                <div className="flex items-center">
                  <CopyIconButton text={subj.hpi} label="HPI" />
                  {onPost && (
                    <PostIconButton
                      onClick={handleSectionPost(
                        "History of Present Illness",
                        subj.hpi
                      )}
                      disabled={!subj.hpi}
                      globalStatus={
                        sectionStatuses["History of Present Illness"] || "idle"
                      }
                      postResetKey={postResetKey}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {subj.hpi}
              </p>
            </>
          )}

          {(subj.family_history ||
            subj.surgical_history ||
            subj.social_history) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-slate-100">
              {subj.family_history && (
                <div className="space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Family History</p>
                  <p className="text-sm text-slate-700">{subj.family_history}</p>
                </div>
              )}
              {subj.surgical_history && (
                <div className="space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Surgical History</p>
                  <p className="text-sm text-slate-700">{subj.surgical_history}</p>
                </div>
              )}
              {subj.social_history && (
                <div className="space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Social History</p>
                  <p className="text-sm text-slate-700">{subj.social_history}</p>
                </div>
              )}
            </div>
          )}

          {subj.ros && (
            <>
              <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-slate-100">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Review of Systems (ROS)</p>
                <div className="flex items-center">
                  <CopyIconButton text={subj.ros} label="Review of Systems" />
                  {onPost && (
                    <PostIconButton
                      onClick={handleSectionPost("Review of Systems", subj.ros)}
                      disabled={!subj.ros}
                      globalStatus={sectionStatuses["Review of Systems"] || "idle"}
                      postResetKey={postResetKey}
                    />
                  )}
                </div>
              </div>
              <div className="space-y-1 bg-slate-50/50 p-3 rounded-lg border border-slate-100">{renderROS(subj.ros)}</div>
            </>
          )}
        </>
      ) : (
        <div className="space-y-4 bg-slate-50/40 p-4 rounded-xl border border-slate-200">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Chief Complaint</label>
            <Textarea
              rows={2}
              className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
              placeholder="Enter Chief Complaint..."
              value={subj.chief_complaint || ""}
              onChange={(e) => setField("chief_complaint", e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">History of Present Illness (HPI)</label>
            <Textarea
              rows={4}
              className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
              placeholder="Enter History of Present Illness..."
              value={subj.hpi || ""}
              onChange={(e) => setField("hpi", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Family History</label>
              <Textarea
                rows={2}
                className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
                placeholder="Family History..."
                value={subj.family_history || ""}
                onChange={(e) => setField("family_history", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Surgical History</label>
              <Textarea
                rows={2}
                className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
                placeholder="Surgical History..."
                value={subj.surgical_history || ""}
                onChange={(e) => setField("surgical_history", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Social History</label>
              <Textarea
                rows={2}
                className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
                placeholder="Social History..."
                value={subj.social_history || ""}
                onChange={(e) => setField("social_history", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Review of Systems (ROS)</label>
            <Textarea
              rows={4}
              className="w-full rounded-lg border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 text-sm bg-white shadow-sm"
              placeholder="Review of Systems..."
              value={subj.ros || ""}
              onChange={(e) => setField("ros", e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectiveSection